import { SESSION_TRANSPORT_COOKIE, SESSION_TRANSPORT_HEADER, type TokenPair, type WebSession } from '@boardflight/shared';
import { analyticsHeaders } from './analytics-preferences';

/**
 * The dashboard's API client.
 *
 * **A page holds an access token and nothing longer-lived** (`docs/library-first-plan.md` §3).
 * The refresh token is an `HttpOnly` cookie on the API's host, which no script can read: this
 * module asks the API to refresh *from* it, and is shown the next fifteen-minute access token.
 * What is in `localStorage` is that access token, the address and the user id — enough for the
 * synchronous `loadSession()` the router depends on, and worth fifteen minutes to a thief.
 *
 * Access tokens live 15 minutes and the refresh token rotates on every use, so refreshing is
 * serialised: behind one in-flight promise in this tab, and behind a Web Lock across this
 * origin's tabs. Two refreshes racing on one cookie cannot both win, and the loser must not
 * conclude it has been signed out.
 */

const STORAGE_KEY = 'boardflight.session';

/**
 * Where the API lives.
 *
 * The dashboard and the API are **different origins** in production — `boardflight.app` is a
 * static-asset Worker whose SPA fallback answers every unknown path with `index.html`, so a
 * same-origin `/v1/...` would get HTML back instead of reaching the API at all. Build with
 * `VITE_API_BASE_URL=https://api.boardflight.app`.
 *
 * Left unset in development, where Vite proxies `/v1` to a local Worker (see vite.config.ts).
 */
const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export interface Session {
  accessToken: string;
  accessTokenExpiresAt: number;
  /**
   * Only in a session stored before the cookie existed, waiting to be moved into it (`adopt`
   * below). Never written: `saveTokens` leaves it out, so the first refresh is also the last time
   * a page can read one. Empty while the session is held outside the page.
   */
  refreshToken?: string;
  email: string;
  userId: string;
  /**
   * What the account is called, for the one place that says it back to its owner: the dashboard's
   * bar. It rides on the token pair, so it is renewed with every access token, and the account
   * page puts a new one here the moment it is saved (`rememberUsername`). Absent from a session
   * stored before this was kept, until its next refresh.
   */
  username?: string;
  /**
   * Present, and `true`, only for a project admin — whether to draw the link to `/admin`
   * (`docs/admin-plan.md` §D4). Absent for everybody else, because the server never sends the
   * key at all.
   *
   * It rides on the token pair rather than on a request of its own, so it costs nothing at boot
   * and is renewed with every access token — a change at the database takes effect within the
   * fifteen minutes an access token lives, without a re-login. A stale `true` draws a link whose
   * every request 404s, which is the harmless direction: this decides what is worth drawing, and
   * the Worker decides what is allowed to happen.
   */
  admin?: true;
  /**
   * Present, and `true`, only for an account on the analytics exclusion list — this browser then
   * sends no product analytics while it is signed in (`product-analytics.ts`). Rides on the token
   * pair like `admin`, so listing or unlisting takes effect within one access token's life. Never
   * present in a session held outside the page, which has no token pair to carry it: there
   * `product-analytics.ts` asks `GET /v1/me` before it sends anything.
   */
  analyticsExcluded?: true;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class NotSignedInError extends Error {
  constructor() {
    super('Not signed in.');
    this.name = 'NotSignedInError';
  }
}

let refreshInFlight: Promise<string> | null = null;
const listeners = new Set<(session: Session | null) => void>();
const signInListeners = new Set<(session: Session) => void>();

/* ------------------------------------------------------- a session held somewhere else */

/**
 * Something outside this page that holds the session (`docs/off-board-plan.md` §2.3, P1).
 *
 * In a browser tab there is none, and everything below this block is exactly as it was: the token
 * pair lives in `localStorage` and this module refreshes it. Inside the desktop app's window the
 * native side holds the session, because a refresh token rotates on every use — two holders would
 * sign each other out — and because a page is not where a long-lived credential should be. There
 * the page asks for an access token and gets exactly that.
 *
 * So while a holder is installed, **nothing here reads, writes or refreshes a stored token**. Who
 * is signed in is kept in memory, with an empty string where the refresh token would be.
 */
export interface SessionHolder {
  /** An access token with at least a minute left in it, or null when nobody is signed in. */
  token(): Promise<{ accessToken: string; accessTokenExpiresAt: number } | null>;
  /** End the session where it is held. */
  signOut?(): Promise<void>;
}

let holder: SessionHolder | null = null;
let held: Session | null = null;
/** Whether the holder in place has said who is signed in yet — see `heldSessionKnown`. */
let heldAnswered = false;

/**
 * Hand the session to a holder, or — with null — take it back.
 *
 * `who` is the account the holder is signed in as, or null for nobody; it is what every
 * `loadSession()` answers from here on, so the pages that ask "is anybody signed in?" need not
 * know the difference. Called again whenever the holder's answer changes.
 *
 * The call that installs a holder says nobody, because nobody is known yet; the next is the
 * holder's first answer, which is who was signed in already. Only after that is a change from
 * nobody to somebody a sign-in — the device-code one the holder ran — and told to `onSignIn`.
 */
export function holdSessionElsewhere(next: SessionHolder | null, who: { userId: string } | null = null): void {
  const same = next !== null && next === holder;
  const signedIn = same && heldAnswered && held === null && who !== null;
  heldAnswered = same;
  holder = next;
  held =
    next && who
      ? {
          // Kept when it is the same account, so a request in flight is not made to ask again.
          accessToken: held?.userId === who.userId ? held.accessToken : '',
          accessTokenExpiresAt: held?.userId === who.userId ? held.accessTokenExpiresAt : 0,
          refreshToken: '',
          email: '',
          userId: who.userId,
        }
      : null;
  listeners.forEach((listener) => listener(loadSession()));
  const now = held;
  if (signedIn && now) signInListeners.forEach((listener) => listener(now));
}

/** Whether the session is held outside this page — the desktop app's window. */
export function sessionHeldElsewhere(): boolean {
  return holder !== null;
}

/**
 * Whether the holder has answered who is signed in. Until it has, `loadSession()` says nobody
 * because nobody is known, not because nobody is there.
 */
export function heldSessionKnown(): boolean {
  return holder !== null && heldAnswered;
}

export function loadSession(): Session | null {
  if (holder) return held;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function saveTokens(tokens: TokenPair | WebSession): Session {
  // A token pair has no place in a page whose session is held elsewhere: storing it would make
  // this page a second holder of a refresh token, which is the one thing the holder exists to
  // prevent. The pair is dropped, and the page goes on believing what the holder says.
  if (holder) {
    return held ?? { accessToken: '', accessTokenExpiresAt: 0, refreshToken: '', email: '', userId: tokens.user.id };
  }
  // No `refreshToken`, whichever shape arrived: it is in the cookie, and a body-transport answer
  // that still carries one (an older API, mid-deploy) is not a reason to start keeping it.
  const session: Session = {
    accessToken: tokens.accessToken,
    accessTokenExpiresAt: tokens.accessTokenExpiresAt,
    email: tokens.user.email,
    userId: tokens.user.id,
    username: tokens.user.username,
    // Spread, so a non-admin's stored session has no `admin` key rather than `admin: false`.
    ...(tokens.user.admin ? { admin: true as const } : {}),
    ...(tokens.user.analyticsExcluded ? { analyticsExcluded: true as const } : {}),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  markSignedIn(true);
  listeners.forEach((listener) => listener(session));
  return session;
}

/**
 * The account was renamed on this page. The stored session says the new name at once, rather than
 * the bar going on calling somebody by a name they just gave up until the next refresh.
 */
export function rememberUsername(username: string): void {
  if (holder) return;
  const session = loadSession();
  if (!session || session.username === username) return;
  const next = { ...session, username };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  listeners.forEach((listener) => listener(next));
}

export function clearSession(): void {
  if (holder) held = null;
  else {
    localStorage.removeItem(STORAGE_KEY);
    markSignedIn(false);
  }
  listeners.forEach((listener) => listener(null));
}

/* ----------------------------------------------------------------- signed in, elsewhere */

/**
 * A mark that this browser has a session, readable from every host of the site.
 *
 * `localStorage` belongs to one origin, and the site has two: somebody signed in at the library
 * arrives at the creators' side with a session cookie this page cannot see and no stored session
 * to say so. The mark is what tells a page it is worth asking — one silent refresh at start
 * (`resumeSession`) — while a visitor without it, which is every anonymous one, costs the API
 * nothing.
 *
 * It is not a credential and grants nothing: it is `1`, anybody can set it, and all a forged one
 * buys is a refresh that fails. Set for the parent domain so both hosts read it; on `localhost`
 * a cookie ignores the port, which is the same thing.
 */
const MARKER = 'bf_in';
const MARKER_MAX_AGE = 60 * 24 * 60 * 60;

function markerDomain(host: string): string {
  // An address or a bare name has no parent to share with.
  if (host === 'localhost' || /^[\d.]+$/.test(host) || host.includes(':') || !host.includes('.')) return '';
  return `; Domain=${host.split('.').slice(-2).join('.')}`;
}

/**
 * Best effort, both ways: a mark that cannot be written costs a visitor to the other host one
 * sign-in they would not otherwise have needed, and must never cost this one theirs.
 */
function markSignedIn(signedIn: boolean): void {
  try {
    const { protocol, hostname } = window.location;
    const secure = protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${MARKER}=${signedIn ? '1' : ''}; Path=/; Max-Age=${signedIn ? MARKER_MAX_AGE : 0}; SameSite=Lax${secure}${markerDomain(hostname)}`;
  } catch {
    // No document, no location, or a frame that may not set cookies.
  }
}

function markedSignedIn(): boolean {
  try {
    return document.cookie.split('; ').includes(`${MARKER}=1`);
  } catch {
    return false;
  }
}

/** Whether a silent sign-in from the cookie is worth trying: marked, and nothing stored here. */
export function sessionMayResume(): boolean {
  return holder === null && typeof window !== 'undefined' && loadSession() === null && markedSignedIn();
}

/**
 * Sign this page in from the cookie, if the browser has a session this origin has not seen.
 *
 * Once, at start, and only where `sessionMayResume` says so. A cookie that turns out to be gone
 * takes the mark with it, so the next page load does not ask again.
 */
export async function resumeSession(): Promise<Session | null> {
  if (!sessionMayResume()) return loadSession();
  try {
    return saveTokens(await withRefreshLock(refreshFromCookie));
  } catch (failure) {
    if (failure instanceof ApiError && (failure.status === 401 || failure.status === 403)) markSignedIn(false);
    return null;
  }
}

export function onSessionChange(listener: (session: Session | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Somebody signed in, here and now: a password, a signup or a provider's sign-in finished on this
 * page (`openSession`), or the holder's own sign-in finished while this page was open.
 *
 * Narrower than `onSessionChange` on purpose. A session also appears when a token is refreshed,
 * when another tab signs in, when a session from the site's other host is resumed and when the
 * desktop app's window loads already signed in — and none of those is anybody signing in.
 */
export function onSignIn(listener: (session: Session) => void): () => void {
  signInListeners.add(listener);
  return () => signInListeners.delete(listener);
}

// Signing out in one tab must sign out the others, rather than leaving them showing an
// account that is no longer signed in. `storage` fires only in *other* tabs, which is
// exactly the audience.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    listeners.forEach((listener) => listener(loadSession()));
  });
}

/* ------------------------------------------------------------------- the cookie's calls */

/**
 * A call that sets, reads or clears the session cookie: the only requests this page sends with
 * credentials, and the only ones that name the cookie transport.
 */
function sessionCall<T>(path: string, body: unknown): Promise<T> {
  return send<T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
    credentials: 'include',
    headers: { [SESSION_TRANSPORT_HEADER]: SESSION_TRANSPORT_COOKIE },
  });
}

/**
 * Sign in — with a password, by signing up, or by finishing a provider's sign-in — and keep the
 * session the way a page keeps one: the refresh token in the cookie, the access token here. Every
 * way a browser signs in comes through here, which is why this is where `onSignIn` is told.
 *
 * Under a holder the page is not where the session lives, so it asks for no cookie and
 * `saveTokens` drops what comes back; nothing in the desktop window reaches this in practice.
 */
export async function openSession(path: string, body: unknown): Promise<Session> {
  if (holder) return saveTokens(await send<TokenPair>(path, { method: 'POST', body: JSON.stringify(body) }));
  const session = saveTokens(await sessionCall<WebSession>(path, body));
  signInListeners.forEach((listener) => listener(session));
  return session;
}

/** How long the loser of a refresh race waits for the browser to hold the winner's cookie. */
const RACE_SETTLE_MS = 700;

/**
 * A new access token from the cookie.
 *
 * A 401 is not yet "signed out". The cookie rotates on every refresh and exactly one of two
 * racing refreshes wins; the lock below keeps this origin's tabs from racing, but a tab on the
 * site's *other* host shares the cookie and no lock. So the loser waits a moment — by which time
 * the winner's `Set-Cookie` has landed — and asks once more. A second 401 is the real answer.
 */
async function refreshFromCookie(): Promise<WebSession> {
  try {
    return await sessionCall<WebSession>('/v1/auth/refresh', {});
  } catch (failure) {
    if (!(failure instanceof ApiError) || failure.status !== 401) throw failure;
    await new Promise((resolve) => setTimeout(resolve, RACE_SETTLE_MS));
    return sessionCall<WebSession>('/v1/auth/refresh', {});
  }
}

/**
 * Move a refresh token this page still holds into the cookie.
 *
 * The session of every browser signed in before the cookie existed. The API rotates it — so what
 * was readable here stops working — and answers with the cookie; `saveTokens` then stores the
 * session without it. A token that is already spent is not the end either: another tab may have
 * adopted it a moment ago, in which case the cookie it left is the session.
 */
async function adopt(refreshToken: string): Promise<WebSession> {
  try {
    return await sessionCall<WebSession>('/v1/auth/adopt', { refreshToken });
  } catch (failure) {
    if (!(failure instanceof ApiError) || failure.status !== 401) throw failure;
    return refreshFromCookie();
  }
}

/**
 * One refresh at a time across every tab of this origin.
 *
 * A Web Lock rather than a flag in storage: it is released by the browser when its holder goes
 * away, so a tab closed mid-refresh cannot leave the others waiting. Where there are no locks —
 * an old browser, a test — the refresh simply runs, and `refreshFromCookie` handles the race.
 */
function withRefreshLock<T>(work: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  return locks ? locks.request('boardflight.session', work) : work();
}

async function accessToken(): Promise<string> {
  if (holder) return heldAccessToken(holder);

  const session = loadSession();
  if (!session) throw new NotSignedInError();

  // A minute of margin, so a request authorized just before expiry does not race it.
  if (session.accessTokenExpiresAt - 60_000 > Date.now()) return session.accessToken;

  refreshInFlight ??= (async () => {
    try {
      return await withRefreshLock(async () => {
        // Whoever held the lock before this tab may have refreshed already, and what it stored
        // is this origin's to use: asking again would only spend the cookie a second time.
        const current = loadSession();
        if (!current) throw new NotSignedInError();
        if (current.accessTokenExpiresAt - 60_000 > Date.now()) return current.accessToken;
        const tokens = current.refreshToken ? await adopt(current.refreshToken) : await refreshFromCookie();
        return saveTokens(tokens).accessToken;
      });
    } catch (failure) {
      if (failure instanceof ApiError && failure.status === 401) {
        // The cookie is spent or gone. Unless another tab has stored a newer session in the
        // meantime, this one is over — and saying so here is what sends the page to sign-in.
        if (loadSession()?.accessToken === session.accessToken) clearSession();
        throw new NotSignedInError();
      }
      throw failure;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * An access token from whoever holds the session. Refreshing is the holder's business; all that is
 * kept here is the token it last gave, until a minute before that one expires.
 */
async function heldAccessToken(from: SessionHolder): Promise<string> {
  if (held && held.accessToken && held.accessTokenExpiresAt - 60_000 > Date.now()) return held.accessToken;
  const token = await from.token();
  // The holder may have been replaced, or the account changed, while it was answering.
  if (holder !== from) throw new NotSignedInError();
  if (!token) {
    if (held) clearSession();
    throw new NotSignedInError();
  }
  if (held) held = { ...held, accessToken: token.accessToken, accessTokenExpiresAt: token.accessTokenExpiresAt };
  return token.accessToken;
}

async function send<T>(path: string, init: RequestInit = {}, token?: string): Promise<T> {
  const headers = analyticsHeaders(new Headers(init.headers));
  if (token) headers.set('authorization', `Bearer ${token}`);
  if (init.body !== undefined && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  const response = await fetch(`${BASE}${path}`, { ...init, headers });
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }

  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string; details?: unknown } })
      ?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'http_error',
      error?.message ?? `Request failed (HTTP ${response.status}).`,
      error?.details,
    );
  }
  return body as T;
}

/** What the API says of an access token whose session is over, though the token's clock is not. */
const SESSION_OVER = new Set(['session_ended', 'invalid_access_token']);

/**
 * Authenticated request.
 *
 * An access token is trusted for fifteen minutes by its own clock, and a session can end sooner
 * than that somewhere this page never sees: on the site's other host, which shares the cookie and
 * not this origin's storage, or from the account's list of sessions on another device. So the one
 * refusal that says *the session behind this token is gone* is not passed straight to the page.
 * The stored token is put aside and the cookie is asked once — which either signs the page in as
 * whoever the browser is signed in as now, and the request goes again, or finds nothing and signs
 * the page out, which is what it should have looked like already.
 *
 * Once, and never for a session held outside the page: the holder's token is the holder's to
 * renew.
 */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await accessToken();
  try {
    return await send<T>(path, init, token);
  } catch (failure) {
    if (holder || !(failure instanceof ApiError) || failure.status !== 401 || !SESSION_OVER.has(failure.code)) throw failure;
    const stored = loadSession();
    // Only the token this request used: another tab may already have moved the session on.
    if (stored?.accessToken === token) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...stored, accessTokenExpiresAt: 0 }));
    }
    return send<T>(path, init, await accessToken());
  }
}

/**
 * Authenticated request whose answer is read as it arrives — the assistant's answers, which are
 * server-sent events (`docs/assistant-plan.md` §2). The same token and the same once-only retry
 * as `api`; a refusal is thrown as an `ApiError` before anything is streamed, and what comes back
 * is the response, for the caller to read.
 */
export async function apiStream(path: string, init: RequestInit = {}): Promise<Response> {
  const open = async (token: string) => {
    const headers = analyticsHeaders(new Headers(init.headers));
    headers.set('authorization', `Bearer ${token}`);
    headers.set('accept', 'text/event-stream');
    if (init.body !== undefined && !headers.has('content-type')) headers.set('content-type', 'application/json');
    const response = await fetch(`${BASE}${path}`, { ...init, headers });
    if (response.ok) return response;
    const body = (await response.json().catch(() => undefined)) as { error?: { code?: string; message?: string; details?: unknown } } | undefined;
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'http_error',
      body?.error?.message ?? `Request failed (HTTP ${response.status}).`,
      body?.error?.details,
    );
  };
  const token = await accessToken();
  try {
    return await open(token);
  } catch (failure) {
    if (holder || !(failure instanceof ApiError) || failure.status !== 401 || !SESSION_OVER.has(failure.code)) throw failure;
    const stored = loadSession();
    if (stored?.accessToken === token) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...stored, accessTokenExpiresAt: 0 }));
    }
    return open(await accessToken());
  }
}

/**
 * Authenticated request for bytes rather than JSON.
 *
 * Exists for one thing: a feedback screenshot, which is entitlement-checked and therefore cannot
 * be an `<img src>` — a browser will not attach a bearer token to one. So the image is fetched
 * here and shown from an object URL, which the caller revokes. Store artwork is public and needs
 * none of this; see `routes/media.ts` for why the two are treated differently.
 */
export async function fetchBlob(path: string): Promise<Blob> {
  const response = await fetch(`${BASE}${path}`, {
    headers: analyticsHeaders(new Headers({ authorization: `Bearer ${await accessToken()}` })),
  });
  if (!response.ok) throw await fileRefusal(response);
  return response.blob();
}

async function fileRefusal(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => undefined)) as
    | { error?: { code?: string; message?: string } }
    | undefined;
  return new ApiError(
    response.status,
    body?.error?.code ?? 'http_error',
    body?.error?.message ?? `Request failed (HTTP ${response.status}).`,
  );
}

/**
 * `fetchBlob` for a file big enough that somebody waits on it: the bytes, with how many have
 * arrived told as they arrive. `total` is null when the answer does not say its length.
 */
export async function fetchBytes(path: string, onProgress?: (loaded: number, total: number | null) => void): Promise<ArrayBuffer> {
  const response = await fetch(`${BASE}${path}`, {
    headers: analyticsHeaders(new Headers({ authorization: `Bearer ${await accessToken()}` })),
  });
  if (!response.ok) throw await fileRefusal(response);
  if (!response.body || !onProgress) return response.arrayBuffer();
  const length = Number(response.headers.get('content-length'));
  const total = Number.isFinite(length) && length > 0 ? length : null;
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress(loaded, total);
  }
  const bytes = new Uint8Array(loaded);
  let at = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, at);
    at += chunk.byteLength;
  }
  return bytes.buffer;
}

/** Unauthenticated request — signup, login, refresh, device polling. */
export async function anonymous<T>(path: string, init: RequestInit = {}): Promise<T> {
  return send<T>(path, init);
}

/**
 * An absolute URL for an API path, for the one case that is not a `fetch`.
 *
 * Signing in with a provider starts by *navigating* to `/v1/auth/:provider/start` — a redirect
 * chain through a consent screen, which no XHR can follow — so that URL has to be built rather
 * than requested. Everything else in this file goes through `send`.
 */
export function apiUrl(path: string): string {
  // OAuth starts with a navigation, which cannot carry our opt-out header. Carry just the
  // choice through the signed state so the provider callback respects it too.
  if (/^\/v1\/auth\/(google|discord)\/start(?:\?|$)/.test(path) && analyticsHeaders(new Headers()).has('x-boardflight-analytics')) {
    return `${BASE}${path}${path.includes('?') ? '&' : '?'}analytics=off`;
  }
  return `${BASE}${path}`;
}

/**
 * A request fired from a page that is being torn down.
 *
 * `keepalive` is the whole point: the browser is asked to finish the request after the document
 * is gone, which an ordinary `fetch` in a `pagehide` handler will not survive. Used for exactly
 * one thing — the Playground telling the server to save and stop when a tab closes mid-turn.
 *
 * Two ways it differs from `api`, both forced by the moment it runs in:
 *
 *  - **The stored access token, never a refresh.** A refresh is a round trip, and nothing that
 *    round-trips will finish here; awaiting one would mean the beacon is never sent at all. A
 *    token within a minute of expiry loses this request, which is the right thing to lose.
 *  - **Nothing to catch.** There is no page left to tell, so the result is dropped rather than
 *    thrown. Callers that can wait should use `api`.
 *
 * `navigator.sendBeacon` would be the obvious tool and cannot be: it sets no headers, so there
 * is nowhere to put the bearer token.
 */
export function beacon(path: string, init: RequestInit = {}): void {
  const session = loadSession();
  if (!session) return;
  const headers = analyticsHeaders(new Headers(init.headers));
  headers.set('authorization', `Bearer ${session.accessToken}`);
  void fetch(`${BASE}${path}`, { ...init, headers, keepalive: true }).catch(() => undefined);
}

export const post = <T>(path: string, body: unknown): Promise<T> =>
  api<T>(path, { method: 'POST', body: JSON.stringify(body) });

export const patch = <T>(path: string, body: unknown): Promise<T> =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

export const put = <T>(path: string, body: unknown): Promise<T> =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body) });

export const del = (path: string): Promise<void> => api<void>(path, { method: 'DELETE' });

export async function signOut(): Promise<void> {
  if (holder) {
    // The holder's session to end, wherever it is kept. There is no refresh token here to revoke.
    await holder.signOut?.().catch(() => undefined);
    clearSession();
    return;
  }
  const session = loadSession();
  if (session) {
    // Best effort: forgetting the local credential is the part that must happen. The cookie's
    // session is revoked and the cookie cleared by the API; a token this page was still holding
    // from before the cookie is revoked the old way, beside it.
    await Promise.all([
      sessionCall('/v1/auth/logout', {}).catch(() => undefined),
      session.refreshToken
        ? anonymous('/v1/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken: session.refreshToken }) }).catch(
            () => undefined,
          )
        : undefined,
    ]);
  }
  clearSession();
}
